import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useMemo, useCallback } from 'react';
import { TranscriptEditorStore } from '../TranscriptEditorStore';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { TranscriptData } from '@src/types.ts/transcript';
import { useStore, useTranscription } from '@src/stores-v2/StoreContext';
import { reaction } from 'mobx';
import { initBrowserDebugger } from '../utils/debug/BrowserDebugger';
// Define the context type
export interface TranscriptEditorContextType {
  editorStore: TranscriptEditorStore | null;
  isReady: boolean;
  currentTime: number;
  setCurrentTime: (time: number) => void;
  refreshSpeakers: () => Promise<void>;
}

// Create the context with default values
const TranscriptEditorContext = createContext<TranscriptEditorContextType>({
  editorStore: null,
  isReady: false,
  currentTime: 0,
  setCurrentTime: () => {},
  refreshSpeakers: async () => {}
});

// Create a provider component for the context
interface TranscriptEditorProviderProps {
  children: ReactNode;
}

export const TranscriptEditorProvider: React.FC<TranscriptEditorProviderProps> = ({ children }) => {
  const rootStore = useStore();
  const videoCreatorStore = useVideoCreatorStore();
  const transcription = useTranscription();
  const [isReady, setIsReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [editorStore, setEditorStore] = useState<TranscriptEditorStore | null>(null);
  const initializationAttempted = useRef(false);

  // Get the story and necessary data
  const story = videoCreatorStore?.story;
  const storyId = useMemo(() => story?.id || '', [story?.id]);
  const albumId = useMemo(() => story?.primaryShowcase?.id || '', [story?.primaryShowcase?.id]);
  
  // Try different audio URL paths based on videoCreatorStore structure
  const audioUrl = useMemo(() => story?.transcription?.audio?.url || '', [story?.transcription?.audio?.url]);
  
  const waveformData = useMemo(() => {
    if (videoCreatorStore?.originalWaveForm) {
      return videoCreatorStore.originalWaveForm;
    }
    return null;
  }, [videoCreatorStore?.originalWaveForm]);


  useEffect(() => {
    if (!editorStore) return;
    const disposer = reaction(
      () => videoCreatorStore.originalWaveForm?.toJSON().data.length,
      () => {
        if (videoCreatorStore.originalWaveForm) {
          editorStore.setWaveformData(videoCreatorStore.originalWaveForm);
        }
      },
      { fireImmediately: true }
    );
    return disposer;          // clean up on unmount
  }, [editorStore]);


  useEffect(() => {
    if (!editorStore) return;
    const disposer = reaction(
      () => transcription?.originalTranscription?.elements?.length,
      () => {
        if (transcription?.originalTranscription) {
          editorStore.setTranscription(transcription?.originalTranscription);
          editorStore.setTranscriptionVersionUrls(transcription.transcriptionVersionUrls);
        }
      },
      { fireImmediately: true }
    );
    return disposer;          // clean up on unmount
  }, [editorStore]);

  
  // Memoize transcriptData to prevent dependency churn
  const transcriptData = useMemo<TranscriptData>(() => {
    return transcription?.originalTranscription || {
      fullText: '',
      version: 0,
      elements: [],
      language: 'en-us'
    };
  }, [transcription?.originalTranscription]);

  // Check if all required resources are available
  const resourceStatus = useMemo(() => ({
    hasStoryId: !!storyId,
    hasAlbumId: !!albumId,
    hasAudioUrl: !!audioUrl,
    hasWaveformData: !!waveformData,
    hasTranscriptData: !!transcriptData,
    hasTranscriptElements: (transcriptData?.elements?.length || 0) > 0,
    isRootStoreReady: !!rootStore.isReady
  }), [storyId, albumId, audioUrl, waveformData, transcriptData, rootStore.isReady]);

  // Modified condition to include only truly essential resources
  const allResourcesReady = useMemo(() => !!(
    resourceStatus.hasStoryId && 
    resourceStatus.hasAlbumId && 
    resourceStatus.hasAudioUrl &&
    resourceStatus.isRootStoreReady
  ), [resourceStatus]);

  // Define createEditorStore outside the effect to avoid stale closures
  const createEditorStore = useCallback(async () => {
    if (initializationAttempted.current) return;
      
    // Minimum resources needed to start: storyId, albumId, audioUrl and rootStore
    // We require audioUrl because TranscriptEditorStore constructor requires it
    if (!storyId || !albumId || !audioUrl || !rootStore.isReady) {
      console.log('Missing minimum resources for editor store creation');
      return;
    }
    
    try {
      initializationAttempted.current = true;
      
      // Create a new TranscriptEditorStore instance with all the required data
      const newEditorStore = new TranscriptEditorStore(
        rootStore,
        storyId,
        albumId,
        audioUrl, 
        waveformData, // May be null, which is fine
        transcriptData,
        'en-us'
      );
      
      // Ensure we have the editor instance before proceeding
      if (!newEditorStore.editor) {
        throw new Error('Editor instance not created properly');
      }
      
      try {
        // Load speakers to initialize SpeakerService - but don't fail if it doesn't work
        await newEditorStore.loadSpeakers();
      } catch (speakerError) {
        console.warn('Failed to load speakers, will continue initialization:', speakerError);
      }      
      
      // Force a waveform refresh if it exists
      if (waveformData) {
        newEditorStore.setWaveformData(waveformData);
      }
      
      setEditorStore(newEditorStore);
      console.log('Editor store created and initialized successfully');
      setIsReady(true);
      
    } catch (error) {
      console.error('Failed to create/initialize editor store:', error);
      // Reset initialization flag to allow retrying
      initializationAttempted.current = false;
    }
  }, [rootStore, storyId, albumId, audioUrl, waveformData, transcriptData]);
  
  // Initialize editor store when all resources are ready
  useEffect(() => {
    if (allResourcesReady && !editorStore) {
      createEditorStore();
    }
  }, [allResourcesReady, editorStore, createEditorStore]);
  
  // Check for transcription updates
  useEffect(() => {
    // If transcription arrives late but we don't have an editor yet, try to create one
    if (!editorStore && 
        transcription?.originalTranscription?.elements && 
        transcription.originalTranscription.elements.length > 0 && 
        !initializationAttempted.current && 
        storyId && albumId && audioUrl) {
      console.log('Transcription elements became available, triggering editor store creation');
      createEditorStore();
    }
  }, [transcription?.originalTranscription, editorStore, createEditorStore, storyId, albumId, audioUrl]);

  // Update the editor store when resources change but store already exists
  useEffect(() => {
    // Still run this effect even if not all resources are ready
    // We want to update the store whenever any individual resource becomes available
    if (!editorStore) return;
        
    // Update store properties if they've changed
    if (storyId && editorStore.getStoryId() !== storyId) {
      editorStore.setStoryId(storyId);
    }
    
    if (albumId && editorStore.getAlbumId() !== albumId) {
      editorStore.setAlbumId(albumId);
    }
    
    if (audioUrl && editorStore.getAudioUrl() !== audioUrl) {
      editorStore.setAudioUrl(audioUrl);
    }
    
    // Update waveform if it becomes available
    if (waveformData && !editorStore.getWaveformData()) {
      editorStore.setWaveformData(waveformData);
    } else if (waveformData) {
      // Ensure waveform is properly set even if it was already there
      editorStore.setWaveformData(waveformData);
    }
    
    // Check if transcription is available from the transcription store
    if (transcription?.originalTranscription) {
      // Check if we need to update transcription data - avoid unnecessary updates
      const currentTranscription = editorStore.getTranscription();
      const incomingTranscription = transcription.originalTranscription;
      
      // Only update if:
      // 1. We don't have a current transcription OR
      // 2. The incoming transcription has elements and current doesn't OR
      // 3. The element counts differ significantly
      if (!currentTranscription || 
          (!currentTranscription.elements?.length && incomingTranscription.elements?.length) ||
          (incomingTranscription.elements?.length && 
           currentTranscription.elements?.length &&
           incomingTranscription.elements.length > currentTranscription.elements.length + 5)) {
        
        editorStore.setTranscription(incomingTranscription);
        editorStore.setTranscriptionVersionUrls(transcription.transcriptionVersionUrls);
      }
    } else if (transcription?.transcriptionLoadingStatus === 'loading') {
      console.log('Transcription is still loading in the store');
    } else {
      console.log('No transcription data available in the store');
    }
    
    // Determine if we're ready based on having a container with segments
    const hasContainer = editorStore.container !== null;
    const hasSegments = hasContainer && editorStore.container?.segments?.length && editorStore.container.segments.length > 0;
    
    if (hasSegments && !isReady && editorStore.container) {
      setIsReady(true);
    }
  }, [editorStore, isReady, storyId, albumId, audioUrl, waveformData?.length, transcription?.originalTranscription]);

  // Function to refresh speakers - can be called when switching to Transcript tab
  const refreshSpeakers = async () => {
    if (!editorStore) return;
    
    try {
      await editorStore.loadSpeakers();
    } catch (error) {
      console.error('Failed to refresh speakers:', error);
    }
  };


  // Provide the context value
  const contextValue: TranscriptEditorContextType = {
    editorStore,
    isReady,
    currentTime,
    setCurrentTime,
    refreshSpeakers
  };

  return (
    <TranscriptEditorContext.Provider value={contextValue}>
      {children}
    </TranscriptEditorContext.Provider>
  );
};

// Create a hook for using the context
export const useTranscriptEditorContext = () => useContext(TranscriptEditorContext);